// Mirrors ssgp-v3-platform/ssgp-v3-contracts/DigitalAssetV3.cs plus the image-lab
// asset surface from ImageGeneration.cs (ImageInfo / ImageUploadResponse /
// ImageDetailsResponse / ImageSource / UserAssetSummary / GalleryItem).

import type { ImageLabOperationType } from './operation';
import type { PaginationInfo } from './session';

// ---------- folder aggregation (B8) ----------

export type AssetKind = 'image' | 'video' | 'audio' | 'text' | 'document' | 'other';
export type AssetSubType = 'Generated' | 'Uploaded' | 'Edited' | string;

// ---------- creative lifecycle + genealogy (Path C, Slice 4) ----------

/** Creative workflow state, distinct from the processing-pipeline `status`.
 *  PascalCase string on the wire (handoff §2). */
export type LifecycleState = 'Draft' | 'Refined' | 'Finalized' | 'Published' | 'Archived';

/** All 5 lifecycle states in promote order — drives the lifecycle <Select>. */
export const LIFECYCLE_STATES: LifecycleState[] = [
  'Draft',
  'Refined',
  'Finalized',
  'Published',
  'Archived',
];

/** How a derived asset relates to its source (handoff §4.4 mapping). */
export type AssetRelationshipType =
  | 'Variation'
  | 'TryOnOutput'
  | 'EditOf'
  | 'InpaintOf'
  | 'OutpaintOf'
  | 'UpscaleOf'
  | 'EnhanceOf'
  | 'StyleTransferOf'
  | 'BackgroundRemovedFrom'
  | 'BakedFilters'
  | 'AnyOther';

export interface AssetRelationshipDto {
  id: string;
  parentAssetId: string;
  childAssetId: string;
  relationshipType: AssetRelationshipType | string;
  operationId?: string;
  createdAt: string;
}

/** GET /api/v3/assets/{id}/family. NB the third field is `family` (the full
 *  bidirectional graph bounded by depth), NOT the spec's `descendants`. */
export interface AssetFamilyResponse {
  assetId: string;
  /** Edges where this asset is the child ("derived from"). */
  parents: AssetRelationshipDto[];
  /** Edges where this asset is the parent ("variations / derivatives"). */
  children: AssetRelationshipDto[];
  /** Full graph reachable both directions, bounded by depth. */
  family: AssetRelationshipDto[];
}

/** PATCH /api/v3/assets/{id}/lifecycle-state response. */
export interface UpdateLifecycleStateResult {
  assetId: string;
  lifecycleState: LifecycleState | string;
}

export interface AssetSummary {
  assetId: string;
  url: string;
  thumbnailUrl?: string;
  /**
   * HMAC-signed companion to `url` the browser can hand to `<img src=…>` /
   * Konva `Image()` directly. Required because native image loads can't
   * attach `x-user-id`. FE prefers this when present, falls back to `url`.
   * See qa/bugs/2026-05-25-asset-files-401-canvas-render.md.
   */
  signedUrl?: string;
  signedThumbnailUrl?: string;
  kind: AssetKind | string;
  width?: number;
  height?: number;
  durationSeconds?: number;
  sizeBytes: number;
  /** "ImageLab", "WriterLab", "FashionStudio", ... — the app-folder key. */
  appAgentType: string;
  subType?: AssetSubType;
  sessionId?: string;
  sessionName?: string;
  studioId?: string;
  promptUsed?: string;
  provider?: string;
  model?: string;
  // Widened on 2026-05-24 (BE BUG-002 fix). Studio-typed metadata
  // (Slice 6/9) lives at `metadata["studio_<id>"]` as a NESTED object —
  // e.g. `metadata.studio_fashion = { material, fabric, garmentType, ... }`.
  // The previous `Record<string, string>` stringified that nested block on
  // serialize, breaking the Studio-details rows in the asset preview modal.
  metadata?: Record<string, unknown>;
  /** Creative lifecycle state (Path C, Slice 4) — additive on AssetSummary. */
  lifecycleState?: LifecycleState | string;
  createdAt: string;
}

export interface AssetFolder {
  /** Composite "{appAgentType}/{sessionId}". */
  id: string;
  appAgentType: string;
  sessionId: string;
  sessionName?: string;
  assetCount: number;
  thumbnailUrl?: string;
  updatedAt: string;
}

export interface AppFolder {
  appAgentType: string;
  name: string;
  folders: AssetFolder[];
  assetCount: number;
}

export interface AssetFolderHierarchy {
  apps: AppFolder[];
}

export interface AssetListPagination {
  page: number;
  pageSize: number;
  totalCount: number;
  totalPages: number;
  hasNextPage: boolean;
  hasPrevPage: boolean;
}

export interface AssetListResponse {
  assets: AssetSummary[];
  pagination: AssetListPagination;
}

// ---------- image-lab asset surface ----------

export type ImageLabImageFormat = 'Png' | 'Jpeg' | 'Webp';
export type ImageSourceType = 'upload' | 'generated' | 'external';

export interface ImageSource {
  type: ImageSourceType | string;
  operationId?: string;
  operationType?: ImageLabOperationType;
  originalFilename?: string;
  externalUrl?: string;
}

export interface ImageInfo {
  id: string;
  url: string;
  thumbnailUrl?: string;
  /** Browser-loadable signed companion. See AssetSummary.signedUrl. */
  signedUrl?: string;
  signedThumbnailUrl?: string;
  width: number;
  height: number;
  /** Lowercased on this surface (e.g. "png"), not the enum form. */
  format: string;
  sizeBytes: number;
}

export interface ImageUploadResponse {
  imageId: string;
  sessionId?: string;
  url: string;
  thumbnailUrl?: string;
  /**
   * HMAC-signed companion to `url` that the browser can hand to
   * `<img src=...>` / Konva `Image()` directly — same contract as
   * `ImageInfo.signedUrl` and `AssetSummary.signedUrl`. Added 2026-05-26 to
   * close the bug chain at `qa/bugs/2026-05-26-invert-filter-produces-black-image.md`
   * and `qa/bugs/2026-05-26-asset-file-401-recurrence-likely-ttl.md`. Without
   * this, upload + bake outputs reach the canvas with no signed URL,
   * consumers fall back to the unsigned `url`, and `crossOrigin='anonymous'`
   * loads 401.
   */
  signedUrl?: string;
  signedThumbnailUrl?: string;
  width: number;
  height: number;
  format: string;
  sizeBytes: number;
  uploadedAt: string;
}

export interface ImageDetailsResponse {
  imageId: string;
  url: string;
  thumbnailUrl?: string;
  width: number;
  height: number;
  format: string;
  sizeBytes: number;
  createdAt: string;
  sessionId?: string;
  source: ImageSource;
  metadata: Record<string, unknown>;
}

export interface UserAssetSummary {
  assetId: string;
  assetUrl: string;
  thumbnailUrl?: string;
  subType: AssetSubType | string;
  width: number;
  height: number;
  format: string;
  sizeBytes: number;
  model?: string;
  provider?: string;
  promptUsed?: string;
  seed?: number;
  sessionId?: string;
  createdAt: string;
  processingTimeMs?: number;
}

export interface UserAssetsListResponse {
  assets: UserAssetSummary[];
  pagination: PaginationInfo;
}

export interface GalleryItemResponse {
  sessionId: string;
  sessionName: string;
  sessionDescription?: string;
  assetId: string;
  assetUrl: string;
  thumbnailUrl?: string;
  subType: AssetSubType | string;
  width: number;
  height: number;
  format: string;
  promptUsed?: string;
  assetCreatedAt: string;
  sessionCreatedAt: string;
  sessionUpdatedAt: string;
  totalAssetsInSession: number;
}

export interface GalleryResponse {
  items: GalleryItemResponse[];
  pagination: PaginationInfo;
}
